import { useRef, useState } from "react";
import { Button, Form, FormGroup, Input } from "reactstrap";
import { MAX_FEEDBACK_LENGTH } from "encharge-domain/lib/helpers/product_feedback_limits";
import { submitFeedback } from "store/persistence/persistFeedback";

export interface FeedbackFormProps {
  /**
   * The question being asked, in our own words. Stored alongside the answer,
   * so a later round asking something else stays comparable rather than being
   * averaged together with this one.
   */
  prompt: string;
  /** Shown above the box. */
  children?: React.ReactNode;
  placeholder?: string;
  submitLabel?: string;
  onSubmitted?: () => void;
}

/**
 * One question, one box, one button.
 *
 * No rating, no picklist, no second page: a list of canned reasons produces
 * "Other: dont need it", which is a shrug rather than a reason. A single open
 * box produces sentences we can actually act on.
 */
const FeedbackForm = ({
  prompt,
  children,
  placeholder = "Whatever you want to tell us…",
  submitLabel = "Send feedback",
  onSubmitted,
}: FeedbackFormProps) => {
  const [message, setMessage] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">(
    "idle"
  );

  // A ref rather than the `state` above: two clicks in the same tick both read
  // the same not-yet-updated state and both get through, and a duplicate row
  // is exactly the kind of dirt that makes the answers harder to read later.
  const inFlight = useRef(false);

  const send = async () => {
    if (!message.trim() || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setState("sending");
    try {
      await submitFeedback({ message: message.trim(), prompt });
      setState("sent");
      onSubmitted?.();
    } catch (e) {
      // Their words stay in the box on failure. Clearing it would lose
      // something they took the trouble to write, to report a problem that is
      // ours rather than theirs.
      setState("failed");
    } finally {
      inFlight.current = false;
    }
  };

  if (state === "sent") {
    return (
      <div className="text-center py-3" data-testid="feedback-sent">
        <strong>Thank you — that is genuinely useful.</strong>
        <div className="text-muted mt-1">We read every one of these.</div>
      </div>
    );
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      {children}
      <FormGroup>
        <Input
          type="textarea"
          rows={4}
          value={message}
          placeholder={placeholder}
          // The server refuses anything longer. Without this the form takes
          // the text, 400s, and shows its generic "try once more" - and
          // retrying the same words can never work, so someone who wrote us a
          // long answer is told to send it again, forever.
          maxLength={MAX_FEEDBACK_LENGTH}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="feedback-message"
        />
      </FormGroup>
      {state === "failed" && (
        <div className="text-danger mb-2" data-testid="feedback-error">
          That did not send. Your words are still here — try once more?
        </div>
      )}
      <Button
        color="primary"
        type="submit"
        disabled={!message.trim() || state === "sending"}
        data-testid="feedback-submit"
      >
        {state === "sending" ? "Sending…" : submitLabel}
      </Button>
    </Form>
  );
};

export default FeedbackForm;
